import { useState } from 'react';
import { useApp } from '@context/AppContext';
import { useSquiggle } from './useSquiggle';
import { Download } from 'lucide-react';

/**
 * Gallery view showing all completed squiggle transformations.
 * Each card can toggle between squiggle-only and completed views.
 * Host can advance to voting (if enabled) or play again / end.
 */
export default function SquiggleGallery() {
  const { state } = useApp();
  const { game, user } = state;
  const { startVoting, playAgain, endGame } = useSquiggle();

  const snapshots = game.snapshots || {};
  const pairs = game.pairs || [];

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 text-center">
          <h2 className="text-xl font-bold text-charcoal font-display">
            🖼️ Gallery
          </h2>
          <p className="text-xs text-slate-400 font-display mt-1">
            Tap a card to toggle between squiggle and transformation
          </p>
        </div>

        {/* Gallery grid */}
        <div className="flex-1 overflow-y-auto p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {pairs.map((pair, idx) => {
              const snap = snapshots[idx];
              if (!snap?.completed) return null;
              return (
                <GalleryCard
                  key={idx}
                  squiggleImage={snap.squiggleOnly}
                  completedImage={snap.completed}
                  squigglerName={snap.squigglerNickname || 'Unknown'}
                  transformerName={snap.transformerNickname || 'Unknown'}
                  pairIndex={idx}
                />
              );
            })}
          </div>
        </div>

        {/* Actions */}
        <div className="px-5 py-4 border-t border-slate-100 space-y-2">
          {user.isHost ? (
            <>
              {game.enableVoting && (
                <button
                  onClick={startVoting}
                  className="w-full px-4 py-2.5 rounded-xl bg-violet-500 text-white text-sm font-semibold font-display hover:bg-violet-600 transition-colors"
                >
                  🗳️ Vote on Transformations
                </button>
              )}
              <div className="flex gap-3">
                <button
                  onClick={playAgain}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-amber text-white text-sm font-semibold font-display hover:bg-amber/90 transition-colors"
                >
                  Play Again
                </button>
                <button
                  onClick={endGame}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-display text-slate-500 hover:bg-slate-50 transition-colors"
                >
                  End Game
                </button>
              </div>
            </>
          ) : (
            <p className="text-sm text-slate-400 font-display text-center">
              Waiting for host...
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Individual gallery card with squiggle/completed toggle.
 */
function GalleryCard({ squiggleImage, completedImage, squigglerName, transformerName, pairIndex }) {
  const [showSquiggle, setShowSquiggle] = useState(false);

  const handleExport = (e) => {
    e.stopPropagation();
    const link = document.createElement('a');
    link.download = `squiggle-pair-${pairIndex + 1}.jpg`;
    link.href = completedImage;
    link.click();
  };

  return (
    <div
      onClick={() => setShowSquiggle(!showSquiggle)}
      className="bg-white rounded-xl overflow-hidden shadow-md border border-slate-100 cursor-pointer hover:shadow-lg transition-shadow"
    >
      {/* Image */}
      <div className="relative">
        <img
          src={showSquiggle ? squiggleImage : completedImage}
          alt={`${transformerName}'s transformation`}
          className="w-full aspect-[4/3] object-cover bg-slate-50"
        />

        {/* Toggle indicator */}
        <div className="absolute top-2 left-2 bg-black/50 text-white text-[10px] font-mono px-2 py-0.5 rounded-full">
          {showSquiggle ? 'Squiggle' : 'Transformed'}
        </div>

        {/* Export button */}
        <button
          onClick={handleExport}
          className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
        >
          <Download className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Attribution */}
      <div className="px-3 py-2">
        <p className="text-xs text-slate-400 font-display">
          <span className="text-charcoal font-semibold">{squigglerName}</span>
          {' → '}
          <span className="text-charcoal font-semibold">{transformerName}</span>
        </p>
      </div>
    </div>
  );
}
